import { useState, useEffect } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useToast } from '../../context/ToastContext'
import { apiCall } from '../../utils/api'

// Initialize Stripe
let stripePromise = null

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      // Fetch publishable key from API
      const response = await apiCall('/v1/payment/config')
      if (response.ok) {
        const data = await response.json()
        stripePromise = loadStripe(data.publishable_key)
      }
    } catch (error) {
      console.error('Error loading Stripe:', error)
    }
  }
  return stripePromise
}

// Payment form component (inside Elements provider)
function PaymentForm({ onSuccess, onClose }) {
  const stripe = useStripe()
  const elements = useElements()
  const { showToast } = useToast()
  const [isProcessing, setIsProcessing] = useState(false)
  const [saveForAutoReload, setSaveForAutoReload] = useState(true)

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!stripe || !elements) {
      showToast('error', 'Stripe has not loaded yet', 'Error')
      return
    }

    try {
      setIsProcessing(true)

      // Confirm setup intent
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/profile/payment-methods`,
        },
        redirect: 'if_required'
      })

      if (error) {
        showToast('error', error.message, 'Payment Method Error')
        setIsProcessing(false)
        return
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Confirm setup intent on backend (fallback if webhook is delayed)
        const confirmResponse = await apiCall('/v1/payment/confirm-setup-intent', {
          method: 'POST',
          body: JSON.stringify({
            setup_intent_id: setupIntent.id
          })
        })

        if (confirmResponse.ok) {
          showToast('success', 'Payment method added successfully', 'Success')
          onSuccess()
        } else {
          const error = await confirmResponse.json()
          showToast('error', error.message || 'Failed to save payment method', 'Error')
        }
      }

    } catch (error) {
      console.error('Error adding payment method:', error)
      showToast('error', 'Failed to add payment method', 'Error')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Stripe Payment Element */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <PaymentElement
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                name: '',
                email: '',
              }
            }
          }}
        />
      </div>

      {/* Auto-Reload Checkbox */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={saveForAutoReload}
            onChange={(e) => setSaveForAutoReload(e.target.checked)}
            className="mt-1 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <div className="flex-1">
            <div className="font-semibold text-green-900 dark:text-green-100">
              Enable for Auto-Reload
            </div>
            <div className="text-sm text-green-800 dark:text-green-200 mt-1">
              Automatically recharge your account when balance falls below your threshold
            </div>
          </div>
        </label>
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🔒</div>
          <div className="text-sm text-blue-900 dark:text-blue-200">
            <strong>Secure Payment Processing</strong>
            <ul className="mt-2 space-y-1">
              <li>✓ Payment details are encrypted and stored by Stripe</li>
              <li>✓ We never see or store your full card number</li>
              <li>✓ You can remove this payment method at any time</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-all transform hover:scale-105 active:scale-95 disabled:transform-none shadow-lg disabled:shadow-none"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </span>
          ) : (
            'Add Payment Method'
          )}
        </button>
      </div>
    </form>
  )
}

// Main modal component
export default function AddPaymentMethodModal({ onClose, onSuccess }) {
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null)
  const [loading, setLoading] = useState(true)
  const [stripePromiseState, setStripePromiseState] = useState(null)
  const { showToast } = useToast()

  // Detect dark mode
  const isDarkMode = document.documentElement.classList.contains('dark')

  useEffect(() => {
    const initialize = async () => {
      try {
        // Load Stripe
        const stripeInstance = await getStripePromise()
        setStripePromiseState(stripeInstance)

        // Create setup intent
        const response = await apiCall('/v1/payment/setup-intent', {
          method: 'POST'
        })

        if (response.ok) {
          const data = await response.json()
          setSetupIntentClientSecret(data.client_secret)
        } else {
          const error = await response.json()
          showToast('error', error.message || 'Failed to initialize payment form', 'Error')
          onClose()
        }
      } catch (error) {
        console.error('Error initializing payment method form:', error)
        showToast('error', 'Failed to initialize payment form', 'Error')
        onClose()
      } finally {
        setLoading(false)
      }
    }

    initialize()
  }, [])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Add Payment Method
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Save a card for quick checkout and auto-reload
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-light leading-none disabled:opacity-50"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {loading ? (
            /* Loading State */
            <div className="space-y-6">
              <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              <div className="flex gap-3">
                <div className="flex-1 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="flex-1 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
            </div>
          ) : setupIntentClientSecret && stripePromiseState ? (
            /* Stripe Elements Form */
            <Elements
              stripe={stripePromiseState}
              options={{
                clientSecret: setupIntentClientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#7c3aed', // primary-600
                    colorBackground: isDarkMode ? '#374151' : '#ffffff', // gray-700 : white
                    colorText: isDarkMode ? '#f9fafb' : '#1f2937', // gray-50 : gray-800
                    colorDanger: '#ef4444',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                  },
                  rules: {
                    '.Input': {
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', // gray-800 : white
                      color: isDarkMode ? '#f9fafb' : '#1f2937', // gray-50 : gray-800
                      border: isDarkMode ? '1px solid #4b5563' : '1px solid #d1d5db', // gray-600 : gray-300
                    },
                    '.Input:focus': {
                      border: `2px solid #7c3aed`, // primary-600
                      outline: 'none',
                    },
                    '.Label': {
                      color: isDarkMode ? '#f9fafb' : '#374151', // gray-50 : gray-700
                      fontWeight: '500',
                    },
                    '.Tab': {
                      backgroundColor: isDarkMode ? '#1f2937' : '#f9fafb', // gray-800 : gray-50
                      color: isDarkMode ? '#d1d5db' : '#6b7280', // gray-300 : gray-500
                      border: isDarkMode ? '1px solid #4b5563' : '1px solid #d1d5db', // gray-600 : gray-300
                    },
                    '.Tab:hover': {
                      color: isDarkMode ? '#f9fafb' : '#1f2937', // gray-50 : gray-800
                    },
                    '.Tab--selected': {
                      backgroundColor: isDarkMode ? '#374151' : '#ffffff', // gray-700 : white
                      color: isDarkMode ? '#f9fafb' : '#1f2937', // gray-50 : gray-800
                      borderColor: '#7c3aed', // primary-600
                    },
                  }
                }
              }}
            >
              <PaymentForm onSuccess={onSuccess} onClose={onClose} />
            </Elements>
          ) : (
            /* Error State */
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Failed to Load Payment Form
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Please try again or contact support if the problem persists
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
